import { Editor, Element, Node, Path, Point, Range, Transforms } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import { isTag } from './navigation';
import { getOverlappingTags } from './overlap';
import { ContinuationTagRef, OverlappingTag } from '../types';

const TAGGABLE_BLOCK_TYPES = new Set(['paragraph', 'list-item']);
const HEADING_FORMATS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

const isTaggableBlock = (n: Node): boolean => {
  if (!Element.isElement(n)) return false;
  const el = n as any;
  if (!TAGGABLE_BLOCK_TYPES.has(el.type)) return false;
  const format = el.formatting?.format;
  if (format && HEADING_FORMATS.has(format)) return false;
  return true;
};

const splitSelectionByBlock = (editor: Editor, selection: Range): { blockPath: Path; range: Range }[] => {
  const segments: { blockPath: Path; range: Range }[] = [];
  const entries = Array.from(Editor.nodes(editor, { at: selection, match: isTaggableBlock }));
  for (const [, blockPath] of entries) {
    const blockRange = Editor.range(editor, blockPath);
    const intersection = Range.intersection(blockRange, selection);
    if (!intersection) continue;
    if (Point.equals(Range.start(intersection), Range.end(intersection))) continue;
    if (Editor.string(editor, intersection).trim() === '') continue;
    segments.push({ blockPath, range: intersection });
  }
  return segments;
};

/**
 * Link two tags via continuation (origin.continuedAt -> target.tagId)
 */
export const linkContinuationTag = (editor: Editor, originTagId: string, targetTagId: string): void => {
  const originEntry = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => isTag(n) && n.tagId === originTagId,
    })
  )[0];

  if (originEntry) {
    const [, originPath] = originEntry;
    Transforms.setNodes(editor, { continuedAt: targetTagId } as any, { at: originPath });
  }
};

/**
 * Insert a tag into the editor (Slate-level only, no API calls)
 * @returns The tag ID used, or null if insertion failed
 */
export const insertTag = (
  editor: Editor,
  tagType: string,
  continuationTag: ContinuationTagRef | null = null,
  tagId: string | null = null,
  successorTagId: string | null = null
): string | null => {
  if (!editor.selection || Range.isCollapsed(editor.selection)) return null;

  const newTagId = tagId || uuidv4();

  // Multi-block selection: emit a continuation chain, one tag wrapper per
  // selected block. iXBRL requires each fragment to carry its own unique
  // id; the head + continuations together represent a single logical fact.
  const blockSegments = splitSelectionByBlock(editor, editor.selection);
  if (blockSegments.length > 1) {
    const segmentIds = [newTagId, ...Array.from({ length: blockSegments.length - 1 }, () => uuidv4())];
    Editor.withoutNormalizing(editor, () => {
      for (let i = blockSegments.length - 1; i >= 0; i--) {
        const isTail = i === blockSegments.length - 1;
        const tag = {
          type: 'tag' as const,
          tagType,
          tagId: segmentIds[i],
          continuedAt: isTail ? successorTagId || undefined : segmentIds[i + 1],
          children: [],
        };
        Transforms.wrapNodes(editor, tag as any, { split: true, at: blockSegments[i].range });
      }
      if (continuationTag) {
        linkContinuationTag(editor, continuationTag.tagId, segmentIds[0]);
      }
    });
    return newTagId;
  }

  // Check for overlapping tags
  const overlapping = getOverlappingTags(editor, editor.selection);

  if (overlapping.length > 0) {
    // Handle overlapping: split existing tags and create continuations
    Editor.withoutNormalizing(editor, () => {
      handleOverlappingTagInsert(editor, tagType, newTagId, overlapping, continuationTag, successorTagId);
    });
    return newTagId;
  }

  // Standard insert (may be nested inside existing tags - that's fine)
  const tag = {
    type: 'tag' as const,
    tagType,
    tagId: newTagId,
    // If there is a successor, this tag continues into it
    continuedAt: successorTagId || undefined,
    children: [],
  };

  Editor.withoutNormalizing(editor, () => {
    Transforms.wrapNodes(editor, tag as any, { split: true, at: editor.selection! });

    // Handle continuation from a previous tag (set predecessor's continuedAt to this tag)
    if (continuationTag) {
      linkContinuationTag(editor, continuationTag.tagId, newTagId);
    }
  });

  return newTagId;
};

/**
 * Calculate the number of segments needed for a tag insertion.
 * This is useful for creating API records before inserting into Slate.
 */
export const calculateTagSegments = (editor: Editor): number => {
  if (!editor.selection || Range.isCollapsed(editor.selection)) return 0;

  const overlapping = getOverlappingTags(editor, editor.selection);

  if (overlapping.length === 0) {
    const blockSegments = splitSelectionByBlock(editor, editor.selection);
    return Math.max(1, blockSegments.length);
  }

  // Calculate segments based on overlapping logic
  const selection = editor.selection;
  const [selStart, selEnd] = Range.edges(selection);

  const boundaries: { inside: Point; outside: Point; type: 'exit' | 'entry' }[] = [];

  for (const overlap of overlapping) {
    const { path: tagPath, overlapType } = overlap;
    const tagRange = Editor.range(editor, tagPath);
    const [tagStart, tagEnd] = Range.edges(tagRange);

    if (overlapType === 'end') {
      const pointAfterTag = Editor.after(editor, tagPath);
      if (pointAfterTag && Point.compare(pointAfterTag, selEnd) <= 0) {
        boundaries.push({ inside: tagEnd, outside: pointAfterTag, type: 'exit' });
      }
    } else if (overlapType === 'start') {
      const pointBeforeTag = Editor.before(editor, tagPath);
      if (pointBeforeTag && Point.compare(selStart, pointBeforeTag) <= 0) {
        boundaries.push({ outside: pointBeforeTag, inside: tagStart, type: 'entry' });
      }
    }
  }

  boundaries.sort((a, b) => Point.compare(a.outside, b.outside));

  const segments: { anchor: Point; focus: Point }[] = [];
  let currentStart = selStart;

  for (const boundary of boundaries) {
    if (boundary.type === 'exit') {
      if (Point.compare(currentStart, boundary.inside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.inside });
      }
      currentStart = boundary.outside;
    } else if (boundary.type === 'entry') {
      if (Point.compare(currentStart, boundary.outside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.outside });
      }
      currentStart = boundary.inside;
    }
  }

  if (Point.compare(currentStart, selEnd) < 0) {
    segments.push({ anchor: currentStart, focus: selEnd });
  }

  const validSegments = segments.filter((seg) => Point.compare(seg.anchor, seg.focus) < 0);

  return Math.max(1, validSegments.length);
};

/**
 * Insert a tag into Slate with pre-defined IDs (for overlapping scenarios)
 */
export const insertTagWithIds = (
  editor: Editor,
  tagType: string,
  continuationTag: ContinuationTagRef | null,
  tagIds: string[],
  successorTagId: string | null = null
): void => {
  if (!editor.selection || Range.isCollapsed(editor.selection)) return;

  const blockSegments = splitSelectionByBlock(editor, editor.selection);
  if (blockSegments.length > 1 && tagIds.length >= blockSegments.length) {
    Editor.withoutNormalizing(editor, () => {
      for (let i = blockSegments.length - 1; i >= 0; i--) {
        const isTail = i === blockSegments.length - 1;
        const tag = {
          type: 'tag' as const,
          tagType,
          tagId: tagIds[i],
          continuedAt: isTail ? successorTagId || undefined : tagIds[i + 1],
          children: [],
        };
        Transforms.wrapNodes(editor, tag as any, { split: true, at: blockSegments[i].range });
      }
      if (continuationTag) {
        linkContinuationTag(editor, continuationTag.tagId, tagIds[0]);
      }
    });
    return;
  }

  const overlapping = getOverlappingTags(editor, editor.selection);

  if (overlapping.length > 0) {
    Editor.withoutNormalizing(editor, () => {
      handleOverlappingTagInsertWithIds(editor, tagType, tagIds, overlapping, continuationTag, successorTagId);
    });
    return;
  }

  // Fallback for simple case (shouldn't happen if segmentCount was > 1)
  const tag = {
    type: 'tag' as const,
    tagType,
    tagId: tagIds[0],
    continuedAt: successorTagId || undefined,
    children: [],
  };

  Editor.withoutNormalizing(editor, () => {
    Transforms.wrapNodes(editor, tag as any, { split: true, at: editor.selection! });
    if (continuationTag) {
      linkContinuationTag(editor, continuationTag.tagId, tagIds[0]);
    }
  });
};

/**
 * Handle overlapping tag insert with pre-defined IDs from the API
 */
const handleOverlappingTagInsertWithIds = (
  editor: Editor,
  tagType: string,
  tagIds: string[],
  overlapping: OverlappingTag[],
  continuationTag: ContinuationTagRef | null,
  successorTagId: string | null = null
): void => {
  const selection = editor.selection!;
  const [selStart, selEnd] = Range.edges(selection);

  const boundaries: { inside: Point; outside: Point; type: 'exit' | 'entry' }[] = [];

  for (const overlap of overlapping) {
    const { path: tagPath, overlapType } = overlap;
    const tagRange = Editor.range(editor, tagPath);
    const [tagStart, tagEnd] = Range.edges(tagRange);

    if (overlapType === 'end') {
      const pointAfterTag = Editor.after(editor, tagPath);
      if (pointAfterTag && Point.compare(pointAfterTag, selEnd) <= 0) {
        boundaries.push({ inside: tagEnd, outside: pointAfterTag, type: 'exit' });
      }
    } else if (overlapType === 'start') {
      const pointBeforeTag = Editor.before(editor, tagPath);
      if (pointBeforeTag && Point.compare(selStart, pointBeforeTag) <= 0) {
        boundaries.push({ outside: pointBeforeTag, inside: tagStart, type: 'entry' });
      }
    }
  }

  boundaries.sort((a, b) => Point.compare(a.outside, b.outside));

  const segments: { anchor: Point; focus: Point }[] = [];
  let currentStart = selStart;

  for (const boundary of boundaries) {
    if (boundary.type === 'exit') {
      if (Point.compare(currentStart, boundary.inside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.inside });
      }
      currentStart = boundary.outside;
    } else if (boundary.type === 'entry') {
      if (Point.compare(currentStart, boundary.outside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.outside });
      }
      currentStart = boundary.inside;
    }
  }

  if (Point.compare(currentStart, selEnd) < 0) {
    segments.push({ anchor: currentStart, focus: selEnd });
  }

  const validSegments = segments.filter((seg) => Point.compare(seg.anchor, seg.focus) < 0);

  if (validSegments.length === 0) {
    const tag = {
      type: 'tag' as const,
      tagType,
      tagId: tagIds[0],
      continuedAt: successorTagId || undefined,
      children: [],
    };
    Transforms.wrapNodes(editor, tag as any, { split: true, at: selection });
    if (continuationTag) {
      linkContinuationTag(editor, continuationTag.tagId, tagIds[0]);
    }
    return;
  }

  // Insert segments in reverse order to preserve document positions
  for (let i = validSegments.length - 1; i >= 0; i--) {
    const segment = validSegments[i];
    const tagId = tagIds[i];
    const isLastSegment = i === validSegments.length - 1;
    const continuedAtId = isLastSegment ? successorTagId || undefined : tagIds[i + 1];

    const tag = {
      type: 'tag' as const,
      tagType,
      tagId,
      continuedAt: continuedAtId,
      children: [],
    };

    const range = { anchor: segment.anchor, focus: segment.focus };
    Transforms.wrapNodes(editor, tag as any, { split: true, at: range });
  }

  if (continuationTag) {
    linkContinuationTag(editor, continuationTag.tagId, tagIds[0]);
  }
};

const handleOverlappingTagInsert = (
  editor: Editor,
  tagType: string,
  newTagId: string,
  overlapping: OverlappingTag[],
  continuationTag: ContinuationTagRef | null,
  successorTagId: string | null = null
): void => {
  const selection = editor.selection!;
  const [selStart, selEnd] = Range.edges(selection);

  const boundaries: { inside: Point; outside: Point; type: 'exit' | 'entry' }[] = [];

  for (const overlap of overlapping) {
    const { path: tagPath, overlapType } = overlap;
    const tagRange = Editor.range(editor, tagPath);
    const [tagStart, tagEnd] = Range.edges(tagRange);

    if (overlapType === 'end') {
      const pointAfterTag = Editor.after(editor, tagPath);
      if (pointAfterTag && Point.compare(pointAfterTag, selEnd) <= 0) {
        boundaries.push({
          inside: tagEnd,
          outside: pointAfterTag,
          type: 'exit',
        });
      }
    } else if (overlapType === 'start') {
      const pointBeforeTag = Editor.before(editor, tagPath);
      if (pointBeforeTag && Point.compare(selStart, pointBeforeTag) <= 0) {
        boundaries.push({
          outside: pointBeforeTag,
          inside: tagStart,
          type: 'entry',
        });
      }
    }
  }

  boundaries.sort((a, b) => {
    const aPoint = a.type === 'exit' ? a.outside : a.outside;
    const bPoint = b.type === 'exit' ? b.outside : b.outside;
    return Point.compare(aPoint, bPoint);
  });

  const segments: { anchor: Point; focus: Point }[] = [];
  let currentStart = selStart;

  // Check if we start inside a tag
  for (const overlap of overlapping) {
    if (overlap.overlapType === 'end') {
      break;
    }
  }

  for (const boundary of boundaries) {
    if (boundary.type === 'exit') {
      if (Point.compare(currentStart, boundary.inside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.inside });
      }
      currentStart = boundary.outside;
    } else if (boundary.type === 'entry') {
      if (Point.compare(currentStart, boundary.outside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.outside });
      }
      currentStart = boundary.inside;
    }
  }

  if (Point.compare(currentStart, selEnd) < 0) {
    segments.push({ anchor: currentStart, focus: selEnd });
  }

  const validSegments = segments.filter((seg) => Point.compare(seg.anchor, seg.focus) < 0);

  if (validSegments.length === 0) {
    const tag = {
      type: 'tag' as const,
      tagType,
      tagId: newTagId,
      continuedAt: successorTagId || undefined,
      children: [],
    };
    Transforms.wrapNodes(editor, tag as any, { split: true, at: selection });
    if (continuationTag) {
      linkContinuationTag(editor, continuationTag.tagId, newTagId);
    }
    return;
  }

  const tagIds = [newTagId];
  for (let i = 1; i < validSegments.length; i++) {
    tagIds.push(uuidv4());
  }

  for (let i = validSegments.length - 1; i >= 0; i--) {
    const segment = validSegments[i];
    const tagId = tagIds[i];
    const isLastSegment = i === validSegments.length - 1;
    const continuedAtId = isLastSegment ? successorTagId || undefined : tagIds[i + 1];

    const tag = {
      type: 'tag' as const,
      tagType,
      tagId,
      continuedAt: continuedAtId,
      children: [],
    };

    const range = { anchor: segment.anchor, focus: segment.focus };
    Transforms.wrapNodes(editor, tag as any, { split: true, at: range });
  }

  if (continuationTag) {
    linkContinuationTag(editor, continuationTag.tagId, newTagId);
  }
};

/**
 * Insert a continuation of an existing tag at the current selection
 */
export const insertContinuation = (editor: Editor, sourceTag: ContinuationTagRef): void => {
  if (!editor.selection || Range.isCollapsed(editor.selection)) return;
  insertTag(editor, sourceTag.tagType, sourceTag);
};
